<template>
  <GcBaseLayout
    :title="'Inshell Product'"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Inshell Product'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Inshell Product'"
        :secondaryLabel="undefined"
      />
    </template>
    <div class="split-view-template css-v2 split-view inshell-product">
      <GcSplitView
        :showLeftContainer="true"
        :showRightContainer="showRightPanel"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <GcDataGrid
              :dataSourceConfig="leftGridConfig.gridData.gridOptions.dataSource"
              :columns="leftGridConfig.gridData.columns"
              :gridToolbar="leftGridConfig.gridData.toolbar"
              :padColumn="true"
              :id="'inshell-product-left-grid'"
              :rowClick="onLeftRowClick"
              :exportFileName="leftGridConfig.exportFileName"
              :heightPadding="20"
              :paging="false"
              ref="leftGrid"
              :filterHeightAdjustment="-10"
              v-if="loaded"
            />
          </div>
        </template>

        <template #rightContent>
          <div class="right-panel-container" v-if="showRightPanel">
            <GcFormHeaderToolbar
              :toolbarOptions="rightToolbarOptions"
              :dataModel="toolbarRecord"
              :baseView="baseView"
              :backRoute="leftGridConfig.pageRoute"
              :itemSaveCallBack="onSaveClicked"
            />

            <div class="selected-record-info">
              <GcForm
                :formOptions="{ gcLabelLocation: 'left' }"
                :formItems="identityFormItems"
                :isNormal="true"
                :data-model="selectedRecord"
                :formMode="baseView.mode"
                :formLocation="'normal'"
                :formHeight="140"
              />
            </div>

            <div class="view-card responsive-paddings inshell-product-grid">
              <DxDataGrid
                :data-source="gridRows"
                key-expr="RowKey"
                :show-borders="false"
                :repaint-changes-only="true"
                :column-auto-width="true"
                :columns="getEditableColumns"
                :height="getHeight"
                @content-ready="onGridContentReady"
                @editor-preparing="onEditorPreparing"
                @toolbar-preparing="onGridToolbarPreparing"
                :ref="'rightGrid'"
              >
                <DxLoadPanel :show-indicator="false" :enabled="false" />
                <DxPaging :page-size="100" :enabled="false" />
                <DxPager :visible="false" />
                <DxEditing
                  mode="cell"
                  :allow-updating="isEditing"
                  :allow-adding="false"
                  :allow-deleting="false"
                  :select-text-on-edit-start="true"
                />

                <template #productCellTemplate="{ data }">
                  <div class="product-cell">
                    <span class="product-cell-primary">{{
                      data.data.ProductDescription
                    }}</span>
                    <span class="product-cell-secondary">{{
                      data.data.ProductCode
                    }}</span>
                  </div>
                </template>

                <template #containerTypeCellTemplate="{ data }">
                  <div class="container-type-cell">
                    <span>{{ data.data.ContainerType }}</span>
                    <span
                      v-if="data.data.CannotBeBagReason"
                      class="cannot-be-bag-hint"
                      :title="data.data.CannotBeBagReason"
                      >&#9888;</span
                    >
                  </div>
                </template>
              </DxDataGrid>
            </div>
          </div>
        </template>
      </GcSplitView>
    </div>
  </GcBaseLayout>
</template>

<script>
/*
Description
    inshell-product.vue - Settings > Intake > Inshell Product (Project 2116,
    Almondco Intake Masters Migration). List + filter panel (#76589) - the
    List (Inshell Variety, Kernel Variety, Configured Product Count, Last
    Changed) and its filter panel (Inshell Variety/Kernel Variety tag-boxes,
    both sourced from AlmondcoInshellVarietySummary directly - no separate
    distinct-value lookup endpoint needed, unlike Scheme Prices' own
    AlmondcoSchemeSearch follow-up, since this view is bounded to one row per
    Inshell Variety, not one row per Season+Scheme combination and does not
    grow unboundedly), plus enough route/identity-resolution scaffolding for a
    List row click to open the split-view panel in Review mode showing the
    Inshell/Kernel Variety identity (matches scheme-prices.vue's own Story 1
    precedent). The real editable derived eligible-product grid (Include/
    Container Type/Bags Per Pallet/Filled Bag (kg), Save) is Story 2/3's own
    task and is not built here - the right-panel placeholder below only shows
    a read-only Configured Product Count.

    Route identity - InshellVarietyCode, not a server-computed RecordId.
    Unlike Scheme Prices/Grower Syndicate Splits (whose natural composite keys
    needed a summary-view RecordId indirection since RouteMixin only ever
    parses one :id segment), this table's natural key is already a single,
    URL-safe VARCHAR(4) code (technical-design.md §6: ":id is
    InshellVarietyCode") - no indirection needed. onLeftRowClick keys directly
    on InshellVarietyCode; loadSelectedRecord resolves the full identity row
    via a $filter=InshellVarietyCode eq '{code}' lookup against the summary
    endpoint - covers both the row-click flow and a direct/bookmarked URL.

    No Add New (§3/§6) - every List row already exists in
    SimarlooConsoleVariety (Originator='A'), maintained entirely by
    AlmondcoConsoleVariety_Trigger. No /create route registered
    (router/settings/intake.js).

    Filter panel field resolution (§6/§10): both Inshell Variety and Kernel
    Variety filter tag-boxes point their dataSourceConfig directly at
    api/AlmondcoInshellVarietySummary (the same endpoint powering the List
    grid itself) - technical-design.md §5/§6 says "both sourced from the same
    summary view", and §10 confirms no new ScreenField row is required here
    ("inline dataField-only grid/form items suffice, matching Grower Regions'
    precedent"). Both filter columns use the established screenField:
    "ColumnValue" placeholder trick (see scheme-prices.vue's own Scheme
    column/bin-comments.vue's own Site column) purely to satisfy gc-form.vue's
    screenField resolution requirement for filter fields - it carries no
    meaning of its own here, matching that precedent exactly.

    Known, non-blocking cosmetic note: since AlmondcoInshellVarietySummary is
    graded one row per Inshell Variety (not per Kernel Variety), if more than
    one Inshell Variety ever shares the same Kernel Variety, the Kernel
    Variety filter's tag-box would show that Kernel Variety's name duplicated.
    Not treated as a scaling/correctness bug requiring a dedicated
    distinct-value endpoint (unlike Scheme Prices' own #77290/#77291 follow-up
    fix) - this view's row count is bounded by the small number of Inshell
    Varieties that exist at all (single digits in the real data, per
    technical-design.md §11's FLAG-CONCEPT-08 note), not an ever-growing
    transaction table.

    Detail grid (#76617): a plain DxDataGrid, cell edit mode, bound directly
    to a local gridRows array loaded from AlmondcoInshellEligibleProduct
    (BR-04 - every active product in the variety's category, Included or
    not). No add/remove - every row already exists by construction, unlike
    the sibling Grower Split grids. Per-row conditional editability (BR-05:
    only Included rows are editable; BR-06/BR-07: Container Type disabled
    whenever CannotBeBagReason is non-empty) is enforced in onEditorPreparing
    - DxColumn's own allowEditing only governs whether a column is editable
    at all (Review vs Edit mode), not which individual rows within it are.
    The Cannot-Be-Bag guard-rail is shown inline via a native title tooltip
    on a small warning glyph next to the Container Type value (§6: "shown
    inline... not a blocking alert dialog"), not a separate popup component.
    Save posts the full grid (Included and un-Included rows both, matching
    legacy's own full-grid XML shape) to POST api/Save_AlmondcoInshellProduct
    and returns to Review mode on success - BR-07a's server-side Cannot-Be-Bag
    re-derivation/coercion happens entirely in
    AlmondcoInshellProductBusiness.Save(), not duplicated here beyond the
    client-side minimum-guard mirror (BR-10/BR-11).

History
    02Sep2026 (SS)
        - #76589: 2116 - UI - Inshell Product List and filter panel
        - #76617: 2116 - UI - Inshell Product split-view detail panel
          (Review/Edit modes, eligible-product grid)

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import { FeatureStorage } from "@/_services/storage";
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import { AlmondcoInshellProductService } from "@/_services/api/settings/purchasing/almondco-inshell-product.service";
import {
  DxDataGrid,
  DxEditing,
  DxPaging,
  DxPager,
} from "devextreme-vue/data-grid";
import DxLoadPanel from "devextreme-vue/load-panel";

export default {
  mixins: [RouteMixin, FormMixin],
  components: { DxDataGrid, DxEditing, DxPaging, DxPager, DxLoadPanel },
  data() {
    return {
      ColumnWidth,

      // The selected List row's identity (Inshell/Kernel Variety Code+Name,
      // Configured Product Count), resolved from the route's
      // InshellVarietyCode via a lookup against AlmondcoInshellVarietySummary
      // (see loadSelectedRecord).
      selectedRecord: {},
      toolbarRecord: {},
      selectedRecordLoaded: false,
      loaded: false,

      // The derived eligible-product grid (§8/BR-04) - local, mutable array
      // bound directly to DxDataGrid's data-source, loaded from
      // AlmondcoInshellEligibleProduct?$filter=InshellVarietyCode eq '{code}'
      // (see loadEligibleProducts). RowKey is a client-only synthetic key
      // (ProductCode is already unique per row, but DxDataGrid's key-expr
      // needs a stable value present from the very first render).
      gridRows: [],
      gridRowsLoaded: false,
      nextRowKey: 1,
      rightGridHeight: "auto",
    };
  },

  computed: {
    getHeight() {
      return this.rightGridHeight;
    },
    showRightPanel() {
      return (
        this.baseView.id !== null &&
        this.baseView.id !== undefined &&
        this.baseView.id !== ""
      );
    },

    // §6: grid is read-only in Review mode, editable only in Edit mode -
    // unchecked (un-Included) rows always render read-only regardless of
    // mode (BR-05), enforced separately in onEditorPreparing.
    isEditing() {
      return this.baseView.mode === "edit";
    },

    // §6: no Active/Inactive toggle. AD-Permission's own literal text says
    // "'update'" for the Save gate, but FeatureStorage.checkPermission
    // (feature.storage.js) only ever recognises "create"/"edit"/"review" -
    // "update" falls through every branch and always returns false,
    // permanently hiding Save for every user regardless of actual
    // WritePermission (confirmed against the real source, and matches a
    // known gotcha from an earlier session on this exact codebase). Using
    // "edit" instead - the same WritePermission check AD-Permission's own
    // intent describes, and consistent with the pencil's own check just
    // above (this screen has no create mode at all, §3/§6 - no + ADD NEW -
    // so baseView.mode can never actually equal "create" here either way).
    // Audit left off - AlmondcoInshellVarietySummary only exposes
    // ChangedBy/DateChanged (no CreatedBy/DateCreated), so an audit popup
    // here would show an incomplete history.
    rightToolbarOptions() {
      return {
        closeSplit: true,
        edit: FeatureStorage.checkPermission("AlmondcoInshellProduct", "edit"),
        save: FeatureStorage.checkPermission("AlmondcoInshellProduct", "edit"),
        audit: false,
      };
    },

    // Identity row (§6): Inshell Code, Inshell Name, Kernel Variety (Code +
    // Name) - all read-only, always, sourced from the opened List row (never
    // independently re-derived from a different join - both this and the
    // List itself read from AlmondcoInshellVarietySummary).
    identityFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              dataField: "InshellVarietyCode",
              label: { text: "Inshell Variety Code" },
              editorType: "dxTextBox",
              editorOptions: { readOnly: true },
            },
            {
              dataField: "InshellVarietyName",
              label: { text: "Inshell Variety Name" },
              editorType: "dxTextBox",
              editorOptions: { readOnly: true },
            },
            {
              dataField: "KernelVarietyCode",
              label: { text: "Kernel Variety Code" },
              editorType: "dxTextBox",
              editorOptions: { readOnly: true },
            },
            {
              dataField: "KernelVarietyName",
              label: { text: "Kernel Variety Name" },
              editorType: "dxTextBox",
              editorOptions: { readOnly: true },
            },
          ],
        },
      ];
    },

    // §6 detail grid: Include (checkbox), Inshell Product (Code/Name
    // combined cell, always read-only), Container Type (dropdown Bag/Bin,
    // editable only when Included and CannotBeBagReason is empty; disabled
    // entirely with an inline guard-rail hint otherwise), Bags Per Pallet /
    // Filled Bag (kg) (numeric, editable only when Included). Per-row
    // conditional editability is enforced in onEditorPreparing - allow-
    // updating alone only governs whether the grid is editable at all
    // (Review vs Edit mode), not which individual rows/cells within it are.
    getEditableColumns() {
      return [
        {
          dataField: "Include",
          caption: "Included",
          dataType: "boolean",
          width: 90,
          alignment: "center",
          allowEditing: this.isEditing,
        },
        {
          dataField: "ProductDescription",
          caption: "Inshell Product",
          minWidth: 260,
          allowEditing: false,
          cellTemplate: "productCellTemplate",
        },
        {
          dataField: "ContainerType",
          caption: "Container Type",
          width: 160,
          minWidth: 160,
          allowEditing: this.isEditing,
          editorType: "dxSelectBox",
          editorOptions: {
            dataSource: ["Bag", "Bin"],
          },
          cellTemplate: "containerTypeCellTemplate",
        },
        {
          dataField: "BagsPerPallet",
          caption: "Bags Per Pallet",
          dataType: "number",
          width: 100,
          minWidth: 100,
          allowEditing: this.isEditing,
          editorOptions: {
            min: 1,
            showSpinButtons: false,
            format: "###,###,##0",
          },
        },
        {
          dataField: "FilledBagKg",
          caption: "Filled Bag (kg)",
          dataType: "number",
          format: "#,##0.00",
          width: 100,
          minWidth: 100,
          allowEditing: this.isEditing,
          editorOptions: { min: 0, format: "#,##0.00", showSpinButtons: false },
        },
      ];
    },

    leftGridConfig() {
      return {
        title: "Inshell Product",
        pageRoute: "/inshell-product",
        exportFileName: "Inshell Product List",

        gridData: {
          gridOptions: {
            // Server-driven, matching this wave's established Summary-view
            // binding (scheme-prices.vue/grower-syndicate-splits.vue) -
            // GcDataGrid queries this endpoint directly (OData
            // $filter/$orderby/paging), with Inshell/Kernel Variety filter
            // panel selections applied server-side since both are real
            // columns on the summary view.
            dataSource: {
              endpoint: "AlmondcoInshellVarietySummary",
              serverPaging: true,
              allowGrouping: false,
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            // §6: filter panel open by default - this grid's own id isn't
            // one of GcDataGrid's hardcoded auto-open ids
            // (transaction-list-grid/grid-report-grid/model), so
            // openFilterByDefault must be set explicitly, matching
            // charge-code.vue's own identical Settings > Intake precedent.
            openFilterByDefault: true,
            expand: false,
            // §3/§6: no "+ ADD NEW" - every List row already exists in
            // SimarlooConsoleVariety, no create concept on this screen.
            customButtons: [],
          },

          columns: [
            {
              // Field mapping (user-stories.md Story 1): InshellVarietyCode/
              // InshellVarietyName -> "Inshell Variety", primary/secondary
              // code+name per common.md's coded-lookup convention. Filter
              // option list sourced directly from this same summary endpoint
              // (see this file's own header comment) - "ColumnValue"
              // satisfies gc-form's screenField requirement for filter
              // fields only, same trick as scheme-prices.vue's own Scheme
              // column/bin-comments.vue's own Site column.
              screenField: "ColumnValue",
              gridDataField: "InshellVarietyName",
              filterDataField: "InshellVarietyCode",
              label: "Inshell Variety",
              filterLabel: "Inshell Variety",
              filterIndex: 0,
              type: "String",
              dataSourceConfig: {
                endpoint: "api/AlmondcoInshellVarietySummary",
                sort: { selector: "InshellVarietyCode", asc: true },
              },
              valueExpr: "InshellVarietyCode",
              displayExpr: "InshellVarietyName",
              editorTemplate: {
                primary: [{ dataField: "InshellVarietyName" }],
                secondary: [{ dataField: "InshellVarietyCode" }],
              },
              cellTemplate: "GcRecordCell",
              cellTemplateRecord: {
                primary: [{ dataField: "InshellVarietyName" }],
                secondary: [{ dataField: "InshellVarietyCode" }],
              },
              exportFields: [
                {
                  dataField: "InshellVarietyCode",
                  label: "Inshell Variety Code",
                },
                {
                  dataField: "InshellVarietyName",
                  label: "Inshell Variety Name",
                },
              ],
              allowHiding: false,
              width: ColumnWidth.ProductDescription.width,
              minWidth: ColumnWidth.ProductDescription.minWidth,
              sortIndex: 0,
              sortOrder: "asc",
            },
            {
              // Field mapping (user-stories.md Story 1): KernelVarietyCode/
              // KernelVarietyName -> "Kernel Variety", same primary/secondary
              // combined-cell treatment as the Inshell Variety column above.
              // See this file's own header comment re: the known, non-
              // blocking cosmetic dedup note for this filter's option list.
              screenField: "ColumnValue",
              gridDataField: "KernelVarietyName",
              filterDataField: "KernelVarietyCode",
              label: "Kernel Variety",
              filterLabel: "Kernel Variety",
              filterIndex: 1,
              type: "String",
              dataSourceConfig: {
                endpoint: "api/AlmondcoInshellVarietySummary",
                sort: { selector: "KernelVarietyCode", asc: true },
              },
              valueExpr: "KernelVarietyCode",
              displayExpr: "KernelVarietyName",
              editorTemplate: {
                primary: [{ dataField: "KernelVarietyName" }],
                secondary: [{ dataField: "KernelVarietyCode" }],
              },
              cellTemplate: "GcRecordCell",
              cellTemplateRecord: {
                primary: [{ dataField: "KernelVarietyName" }],
                secondary: [{ dataField: "KernelVarietyCode" }],
              },
              exportFields: [
                {
                  dataField: "KernelVarietyCode",
                  label: "Kernel Variety Code",
                },
                {
                  dataField: "KernelVarietyName",
                  label: "Kernel Variety Name",
                },
              ],
              allowHiding: false,
              width: ColumnWidth.ProductDescription.width,
              minWidth: ColumnWidth.ProductDescription.minWidth,
            },
            {
              // Server-supplied - COUNT of AlmondcoInshellProduct rows
              // sharing this Inshell Variety's Code, computed in
              // AlmondcoInshellVarietySummary.sql. 0 is a meaningful "not yet
              // configured" signal, not hidden (§8's own view comment).
              dataField: "ConfiguredProductCount",
              label: "Product #",
              alignment: "right",
              allowHiding: false,
              width: ColumnWidth.ItemCount.width,
              minWidth: ColumnWidth.ItemCount.minWidth,
              forceWidth: true,
            },
            {
              screenField: "ChangedBy",
              width: ColumnWidth.Username.width,
              minWidth: ColumnWidth.Username.minWidth,
            },
            {
              screenField: "DateCreated",
              label: "Date Changed",
              dataField: "DateChanged",
              allowHiding: false,
              width: ColumnWidth.Date.width,
              minWidth: ColumnWidth.Date.minWidth,
              forceWidth: true,
              allowSearch: false,
            },
          ],
        },
      };
    },
  },

  watch: {
    // Drives both the row-click flow and a direct/bookmarked URL identically -
    // baseView.id is route-derived (RouteMixin), so this fires either way.
    "baseView.id": {
      immediate: true,
      handler(routeId) {
        if (routeId) {
          this.loadSelectedRecord(routeId);
        } else {
          this.selectedRecord = {};
          this.toolbarRecord = {};
          this.selectedRecordLoaded = false;
          this.gridRows = [];
          this.gridRowsLoaded = false;
        }
        this.setRightGridHeight();
      },
    },
    "baseView.mode": {
      handler() {
        this.setRightGridHeight();
      },
    },
  },

  async created() {
    this.showLoading(true);
    this.$nextTick(() => {
      this.showLoading(false);
      this.loaded = true;
    });
  },

  methods: {
    backButtonFunction() {
      this.$router.push({ path: "/settings/intake" });
    },

    onLeftRowClick(e) {
      if (!e.data) return;
      this.onRowClicked(e, {
        page: "inshell-product",
        mode: "review",
        idProperty: "InshellVarietyCode",
      });
    },

    // §5/§9: AlmondcoInshellVarietySummary, filtered to a single row's
    // InshellVarietyCode - resolves the identity row (Inshell/Kernel Variety
    // Code+Name, Configured Product Count) on baseView.id change, whether
    // that's a row click or a direct/bookmarked URL.
    async loadSelectedRecord(inshellVarietyCode) {
      this.selectedRecordLoaded = false;
      this.showLoading(true);
      try {
        const res =
          await AlmondcoInshellProductService.getInshellVarietySummary(
            inshellVarietyCode,
          );
        const rows = (res.data && res.data.value) || [];
        this.selectedRecord = rows[0] || {};
        this.toolbarRecord = this.selectedRecord;
        if (this.selectedRecord.InshellVarietyCode) {
          await this.loadEligibleProducts(
            this.selectedRecord.InshellVarietyCode,
          );
        } else {
          this.gridRows = [];
        }
      } catch (err) {
        this.selectedRecord = {};
        this.toolbarRecord = {};
        this.gridRows = [];
      } finally {
        this.showLoading(false);
        this.selectedRecordLoaded = true;
      }
    },

    // §5/§9: AlmondcoInshellEligibleProduct, filtered to the opened variety -
    // the derived detail grid's own load call (BR-04). Unconfigured products
    // are pre-populated with defaults (ContainerType='Bag', BagsPerPallet=1,
    // FilledBagKg=1.00) by the view itself, not here.
    async loadEligibleProducts(inshellVarietyCode) {
      this.gridRowsLoaded = false;
      try {
        const res = await AlmondcoInshellProductService.getEligibleProducts(
          inshellVarietyCode,
        );
        const rows = (res.data && res.data.value) || [];
        this.gridRows = rows.map((r) => ({
          RowKey: this.nextRowKey++,
          ProductCode: r.ProductCode,
          ProductDescription: r.ProductDescription,
          Include: r.Include === "Y",
          ContainerType: r.ContainerType,
          BagsPerPallet: r.BagsPerPallet,
          FilledBagKg: r.FilledBagKg,
          CannotBeBagReason: r.CannotBeBagReason,
          RecordId: r.RecordId,
        }));
      } catch (err) {
        this.gridRows = [];
      } finally {
        this.gridRowsLoaded = true;
      }
    },

    // §6: Container Type/Bags Per Pallet/Filled Bag (kg) are editable only
    // when the row is Included (BR-05) - allow-updating alone only governs
    // whether the grid is editable at all (Review vs Edit mode), so per-row
    // disabling for un-Included rows, and Container Type's own Cannot-Be-Bag
    // guard (BR-06/BR-07), both happen here.
    onEditorPreparing(e) {
      if (e.parentType !== "dataRow") return;
      if (
        e.dataField !== "ContainerType" &&
        e.dataField !== "BagsPerPallet" &&
        e.dataField !== "FilledBagKg"
      ) {
        return;
      }
      if (!e.row.data.Include) {
        e.editorOptions.disabled = true;
      }
      if (e.dataField === "ContainerType" && e.row.data.CannotBeBagReason) {
        e.editorOptions.disabled = true;
      }
    },

    onGridContentReady() {
      this.$nextTick(() => {
        this.setRightGridHeight();
      });
    },

    // This grid has no toolbar-level actions of its own (no add/remove, no
    // search/export) - hides DxDataGrid's own default toolbar entirely,
    // matching grower-syndicate-splits.vue's own identical grid shape. The
    // CSS height:0 rule alone (see the .inshell-product-grid style block)
    // only collapses the toolbar's own box - its icons stay in the DOM and
    // visually bleed into the column headers below without this.
    onGridToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },

    setRightGridHeight() {
      setTimeout(() => {
        const rightPanel = document.querySelector(".filter-split-container");
        if (!this.showRightPanel || !rightPanel) return;
        const headerInfo = document.querySelector(".selected-record-info");
        const formHeight = headerInfo ? headerInfo.clientHeight : 0;

        this.rightGridHeight =
          rightPanel.clientHeight - formHeight - 10 - 40 - 12 - 12;

        this.$nextTick(() => {
          const grid = this.$refs.rightGrid;
          if (grid && grid.instance) {
            grid.instance.updateDimensions();
          }
        });
      }, 200);
    },

    // §5/§7 (BR-10/BR-11): client-side mirror of the server's own minimum
    // guards - reduces round-trips, never the sole authority (the server
    // re-validates regardless).
    validateBeforeSave() {
      const includedRows = this.gridRows.filter((r) => r.Include);
      for (const row of includedRows) {
        if (!(row.BagsPerPallet >= 1)) {
          return `Bags Per Pallet must be at least 1 for Product ${row.ProductCode}.`;
        }
        if (!(row.FilledBagKg >= 0)) {
          return `Filled Bag (kg) cannot be negative for Product ${row.ProductCode}.`;
        }
      }
      return null;
    },

    // itemSaveCallBack(isValid, brokenRules) per GcFormHeaderToolbar's own
    // save button contract - isValid/brokenRules describe the toolbar's own
    // (empty, here) form, not our grid, so both are unused.
    async onSaveClicked() {
      const activeElement = document.activeElement;
      if (activeElement) {
        activeElement.blur();
      }
      await this.$nextTick();

      const blockingMessage = this.validateBeforeSave();
      if (blockingMessage) {
        await this.$alert(blockingMessage, "Unable to Save");
        return;
      }

      this.showLoading(true);
      try {
        const payload = {
          InshellVarietyCode: this.selectedRecord.InshellVarietyCode,
          Rows: this.gridRows.map((r) => ({
            ProductCode: r.ProductCode,
            Include: r.Include ? "Y" : "N",
            ContainerType: r.ContainerType,
            BagsPerPallet: r.BagsPerPallet,
            FilledBagKg: r.FilledBagKg,
            RecordId: r.RecordId,
          })),
        };

        await AlmondcoInshellProductService.saveInshellProduct(payload);

        this.setIgnoreRouteChangeConfirmation();
        await this.$router.push({
          path: `${this.leftGridConfig.pageRoute}/review/${this.selectedRecord.InshellVarietyCode}`,
        });
      } catch (err) {
        var data =
          (err && err.data) || (err && err.response && err.response.data);
        var message =
          "An error occurred while saving the Inshell Product configuration.";
        if (Array.isArray(data) && data.length > 0) {
          message = data
            .map(function (e) {
              return e.ErrorMessage || e;
            })
            .join(" ");
        } else if (typeof data === "string" && data.length > 0) {
          message = data;
        }
        await this.$alert(message, "Unable to Save");
      } finally {
        this.showLoading(false);
        this.setRightGridHeight();

        if (this.baseView.id) {
          this.loadSelectedRecord(this.baseView.id);
        }
      }
    },
  },
};
</script>

<style lang="scss">
.inshell-product {
  // Bug fix (confirmed live, 03Sep2026): GcSplitView's right flex item
  // (.split-container-child.right-container, shared/global CSS - see
  // gc-split-view.vue) is flex: 1 1 0px, but flex items have an implicit
  // min-width: auto that refuses to shrink below their content's intrinsic
  // width. The detail grid's own columns (Include+Container Type+Bags Per
  // Pallet+Filled Bag (kg), ~810px combined) exceeded the available panel
  // width on real viewports, so the flex item never shrank to fit - it
  // pushed the whole right panel (including the header toolbar's edit/close
  // icons) off past the visible browser edge instead of scrolling
  // internally. Scoped to this screen only (via the .inshell-product
  // ancestor selector) rather than touching gc-split-view.vue's own shared
  // style, which every other split-view screen in the app also depends on.
  .split-container-child.right-container {
    min-width: 0;
  }

  .selected-record-info {
    padding: 12px 0px 12px 0px;

    .dx-form {
      background-color: white;
      padding: 12px;
    }
  }

  .inshell-product-grid {
    padding: unset !important;
    overflow-x: auto;

    .dx-datagrid-header-panel {
      height: 0px !important;
    }
  }

  .product-cell-primary {
    display: block;
  }

  .product-cell-secondary {
    display: block;
    font-size: 11px;
    color: #667085;
  }

  .cannot-be-bag-hint {
    margin-left: 6px;
    color: #a3222f;
    cursor: help;
  }
}
</style>
